/**
 * Utility functions for checking user activation status for Lc allocation
 * 
 * A user is considered "activated" if they meet ALL of the following criteria:
 * 1. execmember: true (Executive member)
 * 2. userType: is not 8
 * 3. points: more than 0
 */

/**
 * Check if a user is activated to earn Lc
 * @param {Object} userData - User data object
 * @param {boolean} userData.execmember - Whether user is an executive member
 * @param {number} userData.userType - User type (should not be 8)
 * @param {number} userData.points - User's current points (should be > 0)
 * @returns {boolean} - True if user is activated, false otherwise
 */
export const isUserActivated = (userData) => {
  if (!userData) {
    return false;
  }

  const hasExecMember = userData.execmember === true;
  const hasValidUserType = userData.userType !== 8;
  const hasPoints = userData.points && userData.points > 0;

  return hasExecMember && hasValidUserType && hasPoints;
};

/**
 * Get Lc allocation scenario based on owner and user activation status
 * @param {boolean} ownerActivated - Whether the post/lead owner is activated
 * @param {boolean} userActivated - Whether the user doing the action is activated
 * @returns {string} - Scenario type: 'both_activated', 'owner_only', 'none'
 */
export const getLcAllocationScenario = (ownerActivated, userActivated) => {
  if (ownerActivated && userActivated) {
    return 'both_activated'; // Normal 70/30% distribution
  } else if (ownerActivated && !userActivated) {
    return 'owner_only'; // Owner gets 70%, user gets nothing
  } else {
    return 'none'; // Nobody gets points
  }
};

/**
 * Check activation status for both owner and user
 * @param {Object} ownerData - Post/lead owner's data
 * @param {Object} userData - User's data (person doing the action)
 * @returns {Object} - Object with activation status and scenario
 */
export const checkActivationStatus = (ownerData, userData) => {
  const ownerActivated = isUserActivated(ownerData);
  const userActivated = isUserActivated(userData);
  const scenario = getLcAllocationScenario(ownerActivated, userActivated);

  return {
    ownerActivated,
    userActivated,
    scenario
  };
};